import React, { memo } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
  Fade,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  HourglassEmpty as DebouncingIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { SPACING } from '../../../theme';
import PropTypes from 'prop-types';
import { createIOSButtonStyles } from '../../../theme/iosButtonStyles';
import { useTheme } from '@mui/material/styles';

const FiltersBar = ({
  loading,
  searchTerm,
  onSearchChange,
  category,
  onCategoryChange,
  status,
  onStatusChange,
  sortBy,
  onSortByChange,
  onRefresh,
  isDebouncing = false,
  minSearchChars = 3,
  refreshing = false,
  hideStatusFilter = false,
}) => {
  // Get theme for iOS button styles
  const theme = useTheme();
  const iosButtonStyles = createIOSButtonStyles(theme);

  // Determine search field state
  const isSearching = loading && searchTerm.length >= minSearchChars;
  const isValidQuery =
    searchTerm.length === 0 || searchTerm.length >= minSearchChars;
  const showDebouncing = isDebouncing && searchTerm.length >= minSearchChars;

  return (
    <Box sx={{ px: SPACING.lg, py: 0 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid
          size={{ xs: 12, md: 3 }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <TextField
            fullWidth
            size="small"
            label="Search templates"
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={`Min ${minSearchChars} characters...`}
            inputProps={{ 'data-testid': 'templates-search' }}
            error={!isValidQuery}
            helperText={
              !isValidQuery
                ? `Enter at least ${minSearchChars} characters`
                : showDebouncing
                  ? 'Typing...'
                  : isSearching
                    ? 'Searching...'
                    : ''
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Fade in={showDebouncing || isSearching}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 24,
                      }}
                    >
                      {showDebouncing ? (
                        <Tooltip title="Waiting for you to finish typing">
                          <DebouncingIcon
                            sx={{
                              fontSize: 18,
                              color: 'text.disabled',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { opacity: 0.4 },
                                '50%': { opacity: 1 },
                              },
                            }}
                          />
                        </Tooltip>
                      ) : isSearching ? (
                        <Tooltip title="Searching templates">
                          <CircularProgress
                            size={16}
                            sx={{ color: 'primary.main' }}
                          />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </Fade>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': {
                transition: 'all 0.2s ease',
              },
            }}
          />
        </Grid>
        <Grid
          size={{ xs: 12, md: 2 }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              data-testid="templates-filter-category"
              value={category}
              onChange={e => onCategoryChange(e.target.value)}
              label="Category"
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="insight_generation">Insight Generation</MenuItem>
              <MenuItem value="insight_summary">Insight Summary</MenuItem>
              <MenuItem value="comparison_analysis">
                Comparison Analysis
              </MenuItem>
              <MenuItem value="trend_analysis">Trend Analysis</MenuItem>
              <MenuItem value="anomaly_detection">Anomaly Detection</MenuItem>
              <MenuItem value="recommendation_generation">
                Recommendation Generation
              </MenuItem>
              <MenuItem value="data_visualization">Data Visualization</MenuItem>
              <MenuItem value="report_generation">Report Generation</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {!hideStatusFilter && (
          <Grid
            size={{ xs: 12, md: 2 }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                data-testid="templates-filter-status"
                multiple
                value={
                  status === 'all'
                    ? []
                    : Array.isArray(status)
                      ? status
                      : [status]
                }
                onChange={e => {
                  const value = e.target.value;
                  // Handle "all" option - if selected, clear all others and set to 'all'
                  if (value.includes('all')) {
                    onStatusChange('all');
                  } else if (value.length === 0) {
                    // Empty selection means "all"
                    onStatusChange('all');
                  } else {
                    // Filter out "all" if it exists and set the selected statuses
                    const filteredValue = value.filter(v => v !== 'all');
                    onStatusChange(
                      filteredValue.length > 0 ? filteredValue : 'all'
                    );
                  }
                }}
                label="Status"
                renderValue={selected => {
                  if (
                    status === 'all' ||
                    !Array.isArray(selected) ||
                    selected.length === 0
                  ) {
                    return 'All Statuses';
                  }
                  return selected.join(', ');
                }}
              >
                <MenuItem value="all">
                  <em>All Statuses</em>
                </MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="deprecated">Deprecated</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid
          size={{ xs: 12, md: 2 }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
              data-testid="templates-sort-by"
              value={sortBy}
              onChange={e => onSortByChange(e.target.value)}
              label="Sort By"
            >
              <MenuItem value="created_desc">Newest First</MenuItem>
              <MenuItem value="created_asc">Oldest First</MenuItem>
              <MenuItem value="name_asc">Name (A-Z)</MenuItem>
              <MenuItem value="name_desc">Name (Z-A)</MenuItem>
              <MenuItem value="updated_desc">Recently Updated</MenuItem>
              <MenuItem value="rating_desc">Rating (High to Low)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{ xs: 12, md: 2 }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={onRefresh}
            disabled={refreshing}
            title="Apply selected filters"
            data-testid="templates-apply-filters"
            sx={{
              // iOS-style secondary button - flat, colors from theme
              ...iosButtonStyles.secondary,
              textTransform: 'none',
              fontFamily:
                "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
              minHeight: '40px',
              height: '40px',
            }}
          >
            {refreshing ? 'Applying…' : 'Apply Filters'}
          </Button>
        </Grid>
        {(loading || refreshing || onRefresh) && (
          <Grid
            size={{ xs: 12, md: 'auto' }}
            sx={{ ml: 'auto', display: 'flex' }}
          >
            <IconButton
              onClick={onRefresh}
              disabled={refreshing}
              title="Refresh templates"
              data-testid="templates-refresh"
            >
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

FiltersBar.propTypes = {
  loading: PropTypes.bool,
  searchTerm: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  category: PropTypes.string.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  status: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  onStatusChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSortByChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  isDebouncing: PropTypes.bool,
  minSearchChars: PropTypes.number,
  refreshing: PropTypes.bool,
};

export default memo(FiltersBar);
